import React, { useEffect, useMemo, useState } from 'react';
import ApiService from '../../services/ApiService';
import AdminLayout from './AdminLayout';
import './AdminPage.css';

const API_DOMAIN = 'https://api.whitewhale.help';

const EMPTY_ANIME = {
    id: 0,
    thumb: '',
    name: '',
    description: '',
    productionDates: '',
    genres: '',
    announce: '',
    pg: 0,
    createdAt: 0,
    rating: 0,
    available: false,
    movie: false,
    hq: false,
    studio: '',
    blacklistedCountries: '',
};

const SERIES_STATUSES = {
    0: 'Загрузка',
    1: 'Ожидание обработки',
    2: 'Обработка',
    3: 'Доступна',
    4: 'Недоступна',
    5: 'Ошибка превью',
    6: 'Невозможно обработать',
    7: 'Файл не найден',
};

const normalizeAnime = (anime = {}) => ({
    ...EMPTY_ANIME,
    ...anime,
    name: anime.name ?? '',
    description: anime.description ?? '',
    productionDates: anime.productionDates ?? '',
    genres: anime.genres ?? '',
    announce: anime.announce ?? '',
    studio: anime.studio ?? '',
    blacklistedCountries: anime.blacklistedCountries ?? '',
    pg: anime.pg ?? 0,
    rating: anime.rating ?? 0,
    available: Boolean(anime.available),
    movie: Boolean(anime.movie),
    hq: Boolean(anime.hq),
});

const formatSize = (size) => {
    const bytes = Number(size);
    if (!Number.isFinite(bytes) || bytes <= 0) return '—';
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('ru-RU');
};

const AdminPage = () => {
    const [animeList, setAnimeList] = useState([]);
    const [selectedAnime, setSelectedAnime] = useState(null);
    const [animeDetails, setAnimeDetails] = useState(EMPTY_ANIME);
    const [searchQuery, setSearchQuery] = useState('');
    const [thumbFile, setThumbFile] = useState(null);
    const [thumbRevision, setThumbRevision] = useState(Date.now());
    const [seriesList, setSeriesList] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [selectedSeason, setSelectedSeason] = useState('all');
    const [isEditingSeriesMode, setEditingSeriesMode] = useState(false);
    const [isLoadingList, setLoadingList] = useState(true);
    const [isLoadingSeries, setLoadingSeries] = useState(false);
    const [isSaving, setSaving] = useState(false);
    const [isCreating, setCreating] = useState(false);
    const [listError, setListError] = useState('');
    const [seriesError, setSeriesError] = useState('');
    const [notice, setNotice] = useState(null);

    const fetchAnimeList = async () => {
        setLoadingList(true);
        setListError('');
        try {
            const response = await ApiService.getAnimeList();
            if (response.type !== 'success') throw new Error(response.message || 'Не удалось загрузить список');
            const items = Array.isArray(response.data) ? response.data : [];
            setAnimeList(items);
            return items;
        } catch (error) {
            console.error('Error fetching anime list:', error);
            setListError('Не удалось загрузить тайтлы. Проверьте сессию и повторите запрос.');
            return [];
        } finally {
            setLoadingList(false);
        }
    };

    useEffect(() => {
        document.title = 'Админ-панель';
        fetchAnimeList();
    }, []);

    const handleLoadSeries = async (animeId) => {
        if (animeId === undefined || animeId === null) return;
        setLoadingSeries(true);
        setSeriesError('');
        try {
            const response = await ApiService.getAnimeSeriesList(animeId);
            if (response.type !== 'success') throw new Error(response.message || 'Ошибка загрузки серий');
            setSeriesList(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching anime series:', error);
            setSeriesList([]);
            setSeriesError('Не удалось загрузить серии.');
        } finally {
            setLoadingSeries(false);
        }
    };

    const handleSelectAnime = (anime) => {
        const normalized = normalizeAnime(anime);
        setSelectedAnime(normalized);
        setAnimeDetails(normalized);
        setThumbFile(null);
        setNotice(null);
        setSelectedStatus('all');
        setSelectedSeason('all');
        setEditingSeriesMode(false);
        handleLoadSeries(normalized.id);
    };

    const handleInputChange = ({ target: { name, value } }) => {
        setAnimeDetails((current) => ({ ...current, [name]: value }));
    };

    const handleFileChange = (event) => {
        setThumbFile(event.target.files?.[0] || null);
    };

    const handleSaveAnime = async () => {
        setSaving(true);
        setNotice(null);
        try {
            const response = await ApiService.editAnime(animeDetails);
            if (response.type !== 'success') throw new Error(response.message || 'Ошибка сохранения');
            if (thumbFile) {
                await ApiService.uploadThumb(thumbFile, animeDetails.id);
                setThumbFile(null);
                setThumbRevision(Date.now());
            }
            const updated = await fetchAnimeList();
            const savedAnime = updated.find((anime) => anime.id === animeDetails.id) || animeDetails;
            const normalized = normalizeAnime(savedAnime);
            setSelectedAnime(normalized);
            setAnimeDetails(normalized);
            setNotice({ type: 'success', text: 'Изменения сохранены' });
        } catch (error) {
            console.error('Error updating anime details:', error);
            setNotice({ type: 'error', text: 'Не удалось сохранить. Проверьте поля и повторите попытку.' });
        } finally {
            setSaving(false);
        }
    };

    const handleCreateAnime = async () => {
        setCreating(true);
        setNotice(null);
        try {
            const response = await ApiService.editAnime({ ...EMPTY_ANIME, name: 'Новый тайтл' });
            if (response?.type && response.type !== 'success') throw new Error(response.message || 'Ошибка создания');
            const items = await fetchAnimeList();
            const created = response?.data || response;
            const createdAnime = items.find((anime) => anime.id === created?.id) || (created?.id ? created : null);
            if (createdAnime) handleSelectAnime(createdAnime);
            setNotice({ type: 'success', text: 'Черновик создан' });
        } catch (error) {
            console.error('Error creating anime:', error);
            setNotice({ type: 'error', text: 'Не удалось создать тайтл.' });
        } finally {
            setCreating(false);
        }
    };

    const handleCopyToken = async () => {
        const token = localStorage.getItem('sessionKey');
        if (!token) {
            setNotice({ type: 'error', text: 'Токен сессии не найден.' });
            return;
        }
        try {
            await navigator.clipboard.writeText(token);
            setNotice({ type: 'success', text: 'Токен скопирован' });
        } catch (error) {
            console.error('Could not copy token:', error);
            setNotice({ type: 'error', text: 'Браузер не разрешил скопировать токен.' });
        }
    };

    const updateSeriesDraft = (seriesId, field, value) => {
        setSeriesList((current) => current.map((series) => (
            series.id === seriesId ? { ...series, [field]: value } : series
        )));
    };

    const handleEditSeries = async (series) => {
        try {
            const response = await ApiService.setSeries(series.id, series.season, series.seriesNum);
            if (response.type !== 'success') throw new Error(response.message || 'Ошибка изменения');
            setNotice({ type: 'success', text: `Серия ${series.seriesNum} обновлена` });
            await handleLoadSeries(selectedAnime.id);
        } catch (error) {
            console.error('Error editing anime series:', error);
            setNotice({ type: 'error', text: 'Не удалось обновить серию.' });
        }
    };

    const handleDeleteSeries = async (series) => {
        const label = `серию ${series.seriesNum}, сезон ${series.season}`;
        if (!window.confirm(`Удалить ${label}? Видеофайл останется на сервере.`)) return;
        try {
            const response = await ApiService.deleteSeries(series.id);
            if (response.type !== 'success') throw new Error(response.message || 'Ошибка удаления');
            setNotice({ type: 'success', text: `Сущность ${label} удалена` });
            await handleLoadSeries(selectedAnime.id);
        } catch (error) {
            console.error('Error deleting anime series:', error);
            setNotice({ type: 'error', text: 'Не удалось удалить серию.' });
        }
    };

    const filteredAnimeList = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return animeList;
        return animeList.filter((anime) => (
            String(anime.name || '').toLowerCase().includes(query) || String(anime.id).includes(query)
        ));
    }, [animeList, searchQuery]);

    const seasons = useMemo(() => [...new Set(seriesList.map((series) => series.season))]
        .filter((season) => season !== undefined && season !== null)
        .sort((a, b) => Number(a) - Number(b)), [seriesList]);

    const filteredSeriesList = useMemo(() => seriesList.filter((series) => (
        (selectedStatus === 'all' || series.status === Number(selectedStatus))
        && (selectedSeason === 'all' || series.season === Number(selectedSeason))
    )), [seriesList, selectedSeason, selectedStatus]);

    const availableCount = animeList.filter((anime) => anime.available).length;

    return (
        <AdminLayout
            eyebrow="Управление контентом"
            title="Аниме"
            lead="Тайтлы, метаданные и состояние обработки серий."
            actions={(
                <>
                    <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={handleCopyToken}>Скопировать токен</button>
                    <button type="button" className="ui-btn ui-btn--sm admin-button" onClick={handleCreateAnime} disabled={isCreating}>
                        {isCreating ? 'Создание…' : 'Создать тайтл'}
                    </button>
                </>
            )}
        >
            <div className="admin-summary" aria-label="Сводка">
                <div><span>Всего</span><strong>{animeList.length}</strong></div>
                <div><span>Доступно</span><strong>{availableCount}</strong></div>
                <div><span>Скрыто</span><strong>{Math.max(animeList.length - availableCount, 0)}</strong></div>
            </div>

            {notice ? <div className={`admin-notice ${notice.type}`} role="status">{notice.text}</div> : null}

            <div className="admin-workspace">
                <aside className="admin-catalog" aria-label="Список тайтлов">
                    <div className="admin-section-heading">
                        <div>
                            <h2>Каталог</h2>
                            <span>{filteredAnimeList.length} из {animeList.length}</span>
                        </div>
                        <button type="button" className="ui-btn ui-btn--ghost ui-btn--sm admin-text-button" onClick={fetchAnimeList} disabled={isLoadingList}>Обновить</button>
                    </div>
                    <div className="admin-search-row">
                        <input
                            type="search"
                            aria-label="Поиск тайта"
                            placeholder="Название или ID"
                            value={searchQuery}
                            onChange={(event) => setSearchQuery(event.target.value)}
                        />
                        {searchQuery ? <button type="button" onClick={() => setSearchQuery('')} aria-label="Очистить поиск">×</button> : null}
                    </div>
                    {isLoadingList ? <div className="admin-state">Загрузка тайтлов…</div> : null}
                    {listError ? (
                        <div className="admin-state error">
                            <p>{listError}</p>
                            <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={fetchAnimeList}>Повторить</button>
                        </div>
                    ) : null}
                    {!isLoadingList && !listError ? (
                        <ul className="admin-anime-list">
                            {filteredAnimeList.map((anime) => (
                                <li key={anime.id}>
                                    <button
                                        type="button"
                                        className={selectedAnime?.id === anime.id ? 'active' : ''}
                                        onClick={() => handleSelectAnime(anime)}
                                    >
                                        <span className="admin-list-title">{anime.name?.trim() || 'Без названия'}</span>
                                        <span className="admin-list-meta">ID {anime.id}</span>
                                        <span className={`admin-availability ${anime.available ? 'published' : 'hidden'}`}>
                                            {anime.available ? 'Доступно' : 'Скрыто'}
                                        </span>
                                    </button>
                                </li>
                            ))}
                            {filteredAnimeList.length === 0 ? <li className="admin-empty-list">Ничего не найдено</li> : null}
                        </ul>
                    ) : null}
                </aside>

                <section className="admin-editor" aria-label="Редактор тайтла">
                    {!selectedAnime ? (
                        <div className="admin-editor-empty">
                            <div className="admin-empty-mark" aria-hidden="true">A</div>
                            <h2>Выберите тайтл</h2>
                            <p>Найдите его по названию или ID в каталоге слева.</p>
                        </div>
                    ) : (
                        <>
                            <div className="admin-editor-header">
                                <div>
                                    <div className="admin-title-line">
                                        <h2>{animeDetails.name || 'Без названия'}</h2>
                                        <span className={`admin-availability ${animeDetails.available ? 'published' : 'hidden'}`}>
                                            {animeDetails.available ? 'Доступно' : 'Скрыто'}
                                        </span>
                                    </div>
                                    <p>ID {animeDetails.id} · создан {formatDate(animeDetails.createdAt)}</p>
                                </div>
                                <button type="button" className="ui-btn ui-btn--sm admin-button" onClick={handleSaveAnime} disabled={isSaving}>
                                    {isSaving ? 'Сохранение…' : 'Сохранить'}
                                </button>
                            </div>

                            <div className="admin-form-layout">
                                <div className="admin-poster-column">
                                    <img
                                        className="admin-poster"
                                        src={`${API_DOMAIN}/anime/getThumb/${animeDetails.id}_thumb.png?v=${thumbRevision}`}
                                        alt={`Обложка: ${animeDetails.name || 'тайтл'}`}
                                    />
                                    <label className="ui-btn ui-btn--secondary ui-btn--sm admin-file-button" htmlFor="admin-thumb-upload">
                                        Заменить обложку
                                    </label>
                                    <input id="admin-thumb-upload" className="admin-file-input" type="file" accept="image/*" onChange={handleFileChange} />
                                    {thumbFile ? <span className="admin-file-name">{thumbFile.name}</span> : null}
                                </div>

                                <div className="admin-fields">
                                    <label className="admin-field full">
                                        <span>Название</span>
                                        <input type="text" name="name" value={animeDetails.name} onChange={handleInputChange} />
                                    </label>
                                    <label className="admin-field full">
                                        <span>Описание</span>
                                        <textarea name="description" value={animeDetails.description} onChange={handleInputChange} />
                                    </label>
                                    <label className="admin-field">
                                        <span>Студия</span>
                                        <input type="text" name="studio" value={animeDetails.studio} onChange={handleInputChange} />
                                    </label>
                                    <label className="admin-field">
                                        <span>Даты выпуска</span>
                                        <input type="text" name="productionDates" value={animeDetails.productionDates} onChange={handleInputChange} />
                                    </label>
                                    <label className="admin-field full">
                                        <span>Жанры</span>
                                        <input type="text" name="genres" value={animeDetails.genres} onChange={handleInputChange} />
                                    </label>
                                    <label className="admin-field">
                                        <span>Возрастной рейтинг</span>
                                        <input type="number" name="pg" min="0" value={animeDetails.pg} onChange={handleInputChange} />
                                    </label>
                                    <label className="admin-field">
                                        <span>Рейтинг</span>
                                        <input type="number" name="rating" min="0" step="0.1" value={animeDetails.rating} onChange={handleInputChange} />
                                    </label>
                                    <label className="admin-field full">
                                        <span>Недоступные страны</span>
                                        <input type="text" name="blacklistedCountries" placeholder="RU, BY" value={animeDetails.blacklistedCountries} onChange={handleInputChange} />
                                        <small>Коды стран через запятую.</small>
                                    </label>
                                    <label className="admin-field full">
                                        <span>Анонс на главной</span>
                                        <input type="text" name="announce" value={animeDetails.announce} onChange={handleInputChange} />
                                    </label>
                                </div>
                            </div>

                            <div className="admin-switches" aria-label="Параметры публикации">
                                {[
                                    ['available', 'Доступен зрителям', 'Тайтл виден в каталоге'],
                                    ['movie', 'Это фильм', 'Без деления на сезоны'],
                                    ['hq', 'Высокое качество', 'Показывать метку HQ'],
                                ].map(([key, title, description]) => (
                                    <label className="admin-switch-row" key={key}>
                                        <span><strong>{title}</strong><small>{description}</small></span>
                                        <input
                                            type="checkbox"
                                            checked={Boolean(animeDetails[key])}
                                            onChange={(event) => setAnimeDetails((current) => ({ ...current, [key]: event.target.checked }))}
                                        />
                                        <span className="admin-switch-control" aria-hidden="true" />
                                    </label>
                                ))}
                            </div>

                            <section className="admin-series-section">
                                <div className="admin-section-heading series-heading">
                                    <div>
                                        <h2>Серии</h2>
                                        <span>{filteredSeriesList.length} из {seriesList.length}</span>
                                    </div>
                                    <label className="admin-edit-toggle">
                                        <input type="checkbox" checked={isEditingSeriesMode} onChange={(event) => setEditingSeriesMode(event.target.checked)} />
                                        <span>Режим редактирования</span>
                                    </label>
                                </div>
                                <div className="admin-series-filters">
                                    <label>
                                        <span>Статус</span>
                                        <select value={selectedStatus} onChange={(event) => setSelectedStatus(event.target.value)}>
                                            <option value="all">Все статусы</option>
                                            {Object.entries(SERIES_STATUSES).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                        </select>
                                    </label>
                                    <label>
                                        <span>Сезон</span>
                                        <select value={selectedSeason} onChange={(event) => setSelectedSeason(event.target.value)}>
                                            <option value="all">Все сезоны</option>
                                            {seasons.map((season) => <option key={season} value={season}>Сезон {season}</option>)}
                                        </select>
                                    </label>
                                </div>

                                {isLoadingSeries ? <div className="admin-state">Загрузка серий…</div> : null}
                                {seriesError ? <div className="admin-state error">{seriesError}</div> : null}
                                {!isLoadingSeries && !seriesError && filteredSeriesList.length === 0 ? <div className="admin-state">Серии по выбранным фильтрам не найдены.</div> : null}
                                <div className="admin-series-list">
                                    {filteredSeriesList.map((series) => (
                                        <article key={series.id} className="admin-series-card">
                                            <img src={`${API_DOMAIN}/anime/getThumb/${series.privateId}_thumb.png`} alt="" />
                                            <div className="admin-series-content">
                                                <div className="admin-series-title-row">
                                                    <h3>Серия {series.seriesNum} · сезон {series.season}</h3>
                                                    <span className={`admin-series-status status-${series.status}`}>{SERIES_STATUSES[series.status] || 'Неизвестно'}</span>
                                                </div>
                                                <dl className="admin-series-meta">
                                                    <div><dt>ID</dt><dd>{series.id}</dd></div>
                                                    <div><dt>Загружено</dt><dd>{formatDate(series.uploadedAt)}</dd></div>
                                                    <div><dt>Качество</dt><dd>{series.videoQuality || '—'}</dd></div>
                                                    <div><dt>Размер</dt><dd>{formatSize(series.size)}</dd></div>
                                                </dl>
                                                {isEditingSeriesMode ? (
                                                    <div className="admin-series-edit">
                                                        <label><span>Серия</span><input type="number" value={series.seriesNum ?? ''} onChange={(event) => updateSeriesDraft(series.id, 'seriesNum', event.target.value)} /></label>
                                                        <label><span>Сезон</span><input type="number" value={series.season ?? ''} onChange={(event) => updateSeriesDraft(series.id, 'season', event.target.value)} /></label>
                                                        <button type="button" className="ui-btn ui-btn--secondary ui-btn--sm admin-button" onClick={() => handleEditSeries(series)}>Применить</button>
                                                    </div>
                                                ) : null}
                                                <div className="admin-series-actions">
                                                    <button type="button" className="ui-btn ui-btn--sm admin-danger-button" onClick={() => handleDeleteSeries(series)}>Удалить сущность</button>
                                                    <span>Видеофайл не будет удален</span>
                                                </div>
                                            </div>
                                        </article>
                                    ))}
                                </div>
                            </section>
                        </>
                    )}
                </section>
            </div>
        </AdminLayout>
    );
};

export default AdminPage;
